import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from 'i18n';

interface LanguageSelectorProps {
  variant?: 'header' | 'inline';
}

const LANGUAGE_LABEL_KEY: Record<SupportedLanguage, string> = {
  el: 'header.languageGreek',
  en: 'header.languageEnglish',
};

const LANGUAGE_DISPLAY: Record<SupportedLanguage, string> = {
  el: 'EL',
  en: 'EN',
};

const LANGUAGE_OPTIONS: SupportedLanguage[] = [...SUPPORTED_LANGUAGES];

const LanguageSelector = ({ variant = 'header' }: LanguageSelectorProps) => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLang: SupportedLanguage = LANGUAGE_OPTIONS.includes(i18n.language as SupportedLanguage)
    ? (i18n.language as SupportedLanguage)
    : 'el';

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (lang: SupportedLanguage) => {
    if (lang !== currentLang) {
      i18n.changeLanguage(lang);
    }
    handleClose();
  };

  return (
    <>
      <Tooltip title={t('header.language')}>
        <IconButton
          onClick={handleOpen}
          aria-label={t('header.language')}
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            color: variant === 'header' ? '#fff' : variant === 'inline' ? '#8E8E93' : 'inherit',
            display: 'flex',
            gap: 0.5,
            fontSize: 14,
            borderRadius: 1,
            px: 1,
            ...(variant === 'inline' && {
              backgroundColor: '#F0F0F2',
              border: '1px solid #E5E5EA',
              '&:hover': { backgroundColor: '#E5E5EA' },
            }),
          }}
        >
          <LanguageOutlinedIcon fontSize="small" />
          <span style={{ fontSize: '0.85rem' }}>{LANGUAGE_DISPLAY[currentLang]}</span>
        </IconButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {LANGUAGE_OPTIONS.map(lang => (
          <MenuItem key={lang} selected={lang === currentLang} onClick={() => handleSelect(lang)}>
            <ListItemIcon sx={{ minWidth: 28 }}>{lang === currentLang ? <CheckOutlinedIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>{t(LANGUAGE_LABEL_KEY[lang])}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
